<script setup lang="ts">
import { computed } from 'vue';
import NoResponseDefault from '../../shared/components/visuals/visualAssets/NoResponseDefault.svg?url';
import NoResponseBinocular from '../../shared/components/visuals/visualAssets/NoResponseBinocular.svg?url';
import NoResponseCupOfCoffee from '../../shared/components/visuals/visualAssets/NoResponseCupOfCoffee.svg?url';
import NoResponseEmptyPlate from '../../shared/components/visuals/visualAssets/NoResponseEmptyPlate.svg?url';
import FlyingCardsABitHeavy from '../../shared/components/visuals/visualAssets/FlyingCardsABitHeavy.svg?url';
import FlyingCardsTooHeavy from '../../shared/components/visuals/visualAssets/FlyingCardsTooHeavy.svg?url';
import DownloadExport from '../../shared/components/visuals/visualAssets/DownloadExport.svg?url';
import DownloadExportExpired from '../../shared/components/visuals/visualAssets/DownloadExportExpired.svg?url';
import NoResponsesFolderWithStars from '../../shared/components/visuals/visualAssets/NoResponseFolderWithStars.svg?url';
import NoResponseTick from '../../shared/components/visuals/visualAssets/NoResponseTick.svg?url';
import NoResponseClipboard from '../../shared/components/visuals/visualAssets/NoResponseClipboard.svg?url';
import NoResponseResonateGoogleCog from '../../shared/components/visuals/visualAssets/NoResponseResonateGoogleCog.svg?url';
import NoResponseSocialHourglass from '../../shared/components/visuals/visualAssets/NoResponseSocialHourglass.svg?url';
import NoResponseHighFive from '../../shared/components/visuals/visualAssets/NoResponseHighFive.svg?url';
import { NoResponsesVariationType } from '@Portal/models/NoResponsesVariationType';
import { NO_RESPONSE_VARIATIONS } from '@Shared/constants';
import { NoResponseVariation } from '@Shared/types/portal/NoResponseVariation';
import { trackAppInsightsEvent } from '@Shared/utils/util';
import { ProcessFlowQueue } from '@Shared/types/portal/ProcessFlow';

type Action = (queue?: ProcessFlowQueue) => void | Promise<void>;
const props = withDefaults(
  defineProps<{
    variation?: NoResponsesVariationType;
    templateVariables?: Record<string, string | undefined>;
    specialAction?: Action | ((nextTab: string) => void);
  }>(),
  { variation: NoResponsesVariationType.Default }
);

// The on-click button is default to track app insights, and run any special actions if they exists
const defaultAction = async () => {
  trackAppInsightsEvent('EmptyStatePage-CallToAction', {
    variation: variation?.value,
    product: 'EmptyStatePage',
    label: 'Clicked empty state page button to go to different state',
    ...props.templateVariables,
  });
  if (!props.specialAction) return;

  if (variation.value.nextTab != undefined) {
    (props.specialAction as (nextTab: string) => void)(variation.value.nextTab);
    return;
  }

  await (props.specialAction as Action)();
};

const variation = computed<NoResponseVariation>(() => {
  return NO_RESPONSE_VARIATIONS.filter((v) => v.name == props.variation)[0];
});
const variationImage = (): string => {
  switch (variation.value.image) {
    case 'EmptyPlate':
      return NoResponseEmptyPlate;
    case 'HighFive':
      return NoResponseHighFive;
    case 'CupOfCoffee':
      return NoResponseCupOfCoffee;
    case 'Binocular':
      return NoResponseBinocular;
    case 'FlyingCardsTooHeavy':
      return FlyingCardsTooHeavy;
    case 'FlyingCardsABitHeavy':
      return FlyingCardsABitHeavy;
    case 'ReadyToDownloadExport':
      return DownloadExport;
    case 'DownloadExportExpired':
      return DownloadExportExpired;
    case 'FolderWithStars':
      return NoResponsesFolderWithStars;
    case 'Tick':
      return NoResponseTick;
    case 'SocialHourglass':
      return NoResponseSocialHourglass;
    case 'Clipboard':
      return NoResponseClipboard;
    case 'ResonateGoogleCog':
      return NoResponseResonateGoogleCog;
    default:
      return NoResponseDefault;
  }
};

// Function to replace placeholders with values from templateVariables
const replaceTemplateVariables = (template: string) => {
  return Object.entries(props.templateVariables ?? {}).reduce(
    (acc, [key, value]) => acc.replace(new RegExp(`{${key}}`, 'g'), value ?? ''),
    template
  );
};

onMounted(() => {
  trackAppInsightsEvent('EmptyStatePage', {
    variation: variation.value?.name,
    product: 'EmptyStatePage',
    label: 'Empty state page shown',
    ...props.templateVariables,
  });
});
</script>

<template>
  <div :id="`EmptyState-${variation.name}`" class="flex flex-row items-center justify-evenly">
    <div
      class="flex lg:flex-row items-center w-252 bg-[#fff] border-rd-5 lg:h-144 mt-10 mb-15 ml-5 mr-5 h-auto flex-col mobile:mr-0 mobile:ml-0"
      :class="{
        'border-1 border-solid': !variation.isSocial,
      }"
      :style="{ 'border-color': variation.colour }"
    >
      <div
        class="flex flex-col h-inherit pt-18 lg:pb-18 lg:pl-21 lg:max-w-125 lg:pr-2 pr-14 pl-14 max-w-175 pb-18 mobile:pl-20 mobile:pr-20"
      >
        <div
          class="text-size-13 font-bold font-family[Montserrat] mobile:text-size-9 line-height-4rem mobile:line-height-3rem"
          :style="{ color: variation.colour }"
        >
          {{ replaceTemplateVariables(variation.headerMessage || '') }}
        </div>
        <div
          class="text-size-5 font-family[Open Sans] font-normal color-[#333] pt-13 mb-7 pr-6 mobile:pt-5 mobile:text-size-4 mobile:mb-2 line-height-1.75rem mobile:line-height-1.5rem"
        >
          {{ replaceTemplateVariables(variation.bodyMessage || '') }}
        </div>
        <div
          v-if="variation.buttonText"
          id="noresponsepage-button"
          @click="defaultAction"
          class="bg-[#C73362] color-[#fff] font-family[Open Sans] text-size-4 font-semibold text-center border-rd-1 pt-3 pb-3 pr-4 pl-4 cursor-pointer w-fit mobile:text-size-3"
        >
          {{ replaceTemplateVariables(variation.buttonText || '') }}
        </div>
        <div class="mt-7 mobile:mt-3" v-if="variation.footerHeadingMessage">
          <span class="text-size-4 font-family[Open Sans] font-bold">{{ variation.footerHeadingMessage }}</span>
          <ul v-for="(option, index) in variation.footerDotPoints" :key="index">
            <li class="list-circle ml-5 mt-2 mobile:line-height-1.5rem" :style="{ color: variation.colour }">
              <span class="color-[#333333] font-normal text-size-4">
                {{ replaceTemplateVariables(option || '') }}
              </span>
            </li>
          </ul>
        </div>
      </div>
      <div class="lg:pl-2 lg:pb-0 pl-0 pb-10 mobile:w-65">
        <img :src="variationImage()" alt="" class="mt-5 mobile:mt-0 mobile:w-70 mobile:h-70" />
      </div>
    </div>
  </div>
</template>
